import {
  Span,
  SpanKind,
  SpanStatusCode,
  context,
  trace,
} from "@opentelemetry/api";
import { SeverityNumber, Logger } from "@opentelemetry/api-logs";
import { OTLPLogExporter } from "@opentelemetry/exporter-logs-otlp-http";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import {
  type Resource,
  resourceFromAttributes,
} from "@opentelemetry/resources";
import {
  BatchLogRecordProcessor,
  LoggerProvider,
} from "@opentelemetry/sdk-logs";
import {
  BatchSpanProcessor,
  WebTracerProvider,
} from "@opentelemetry/sdk-trace-web";

import { featureFlags } from "_/utils/feature-flags";
import * as SemConv from "_/utils/semconv";

// OpenTelemetry uses numerical severity levels, but allows for applications
// to specify their desired severity text. This allows us to use the same
// levels while still applying a consistent text representation that will be
// present in the logs and can be used for more human-friendly filtering.
const SEVERITY_TEXT = {
  [SeverityNumber.UNSPECIFIED]: "UNSPECIFIED",
  [SeverityNumber.TRACE]: "TRACE",
  [SeverityNumber.TRACE2]: "TRACE",
  [SeverityNumber.TRACE3]: "TRACE",
  [SeverityNumber.TRACE4]: "TRACE",
  [SeverityNumber.DEBUG]: "DEBUG",
  [SeverityNumber.DEBUG2]: "DEBUG",
  [SeverityNumber.DEBUG3]: "DEBUG",
  [SeverityNumber.DEBUG4]: "DEBUG",
  [SeverityNumber.INFO]: "INFO",
  [SeverityNumber.INFO2]: "INFO",
  [SeverityNumber.INFO3]: "INFO",
  [SeverityNumber.INFO4]: "INFO",
  [SeverityNumber.WARN]: "WARN",
  [SeverityNumber.WARN2]: "WARN",
  [SeverityNumber.WARN3]: "WARN",
  [SeverityNumber.WARN4]: "WARN",
  [SeverityNumber.ERROR]: "ERROR",
  [SeverityNumber.ERROR2]: "ERROR",
  [SeverityNumber.ERROR3]: "ERROR",
  [SeverityNumber.ERROR4]: "ERROR",
  [SeverityNumber.FATAL]: "FATAL",
  [SeverityNumber.FATAL2]: "FATAL",
  [SeverityNumber.FATAL3]: "FATAL",
  [SeverityNumber.FATAL4]: "FATAL",
} as const;

const TRACER_NAME = "aon3d.webclient";

type OtelSettings = {
  /** Where to send logs and traces, or `null` to send none. */
  endpoint: string | null;
  /** The deployment environment, such as `prod`, which labels them. */
  environment: string;
};

/** A console call, with when it was made. */
type ConsoleCall = {
  severityNumber: SeverityNumber;
  args: unknown[];
  stack: string | undefined;
  timestamp: number;
};

// At most this many console calls are kept while the telemetry settings load,
// so that a slow or failing `/preload` can't use up memory.
const MAX_PENDING_CONSOLE_CALLS = 1000;

// Telemetry's settings come from the server, so console calls made before
// they arrive are kept ("pending"), then sent ("started") or dropped
// ("stopped").
type OtelState =
  | { kind: "idle" }
  | { kind: "pending"; calls: ConsoleCall[] }
  | { kind: "started"; logger: Logger }
  | { kind: "stopped" };

let otelState: OtelState = { kind: "idle" };

const originalConsole = {
  debug: console.debug,
  log: console.log,
  info: console.info,
  warn: console.warn,
  error: console.error,
};

/**
 * Starts capturing console output for telemetry, until `startOtel` sends it
 * or `stopOtel` drops it.
 * This should be called once at the start of the application.
 */
export function initOtel() {
  if (otelState.kind !== "idle") {
    return;
  }
  patchConsole();
  otelState = { kind: "pending", calls: [] };
}

/**
 * Starts sending logs and traces, with the settings the server gives in
 * `/preload`, including the console output captured since `initOtel`. Only
 * the first call to this or `stopOtel` has an effect.
 */
export function startOtel({ endpoint, environment }: OtelSettings) {
  if (otelState.kind !== "pending") {
    return;
  }
  // Installs without a telemetry endpoint, such as Basis Standalone, don't
  // send logs or traces anywhere.
  if (endpoint === null) {
    stopOtel();
    return;
  }

  const resource = resourceFromAttributes({
    [SemConv.ATTR_SERVICE_NAME]: "webclient",
    [SemConv.ATTR_DEPLOYMENT_ENVIRONMENT]: environment,
  });
  const logger = initLogging(endpoint, resource);
  initTracing(endpoint, resource);

  const pendingCalls = otelState.calls;
  otelState = { kind: "started", logger };
  for (const call of pendingCalls) {
    emitLog(logger, call);
  }
}

/**
 * Drops the captured console output and sends no telemetry, for when there's
 * nowhere to send it. Only the first call to this or `startOtel` has an
 * effect.
 */
export function stopOtel() {
  if (otelState.kind !== "pending") {
    return;
  }
  Object.assign(console, originalConsole);
  otelState = { kind: "stopped" };
}

function initLogging(endpoint: string, resource: Resource): Logger {
  const provider = new LoggerProvider({
    resource,
    processors: [
      new BatchLogRecordProcessor({
        exporter: new OTLPLogExporter({
          url: `${endpoint}/v1/logs`,
          headers: {},
          concurrencyLimit: 1,
        }),
        exportTimeoutMillis: 5000,
      }),
    ],
  });

  featureFlags.init("otelLogs", true);

  return provider.getLogger("console");
}

function initTracing(endpoint: string, resource: Resource) {
  const exporter = new OTLPTraceExporter({
    url: `${endpoint}/v1/traces`,
    headers: {},
    concurrencyLimit: 1,
  });

  const processor = new BatchSpanProcessor(exporter, {
    exportTimeoutMillis: 5000,
  });

  const provider = new WebTracerProvider({
    resource,
    spanProcessors: [processor],
  });

  provider.register();

  return provider.getTracer(TRACER_NAME);
}

/**
 * Cross-browser stack frame parser that never throws.
 * Supports Chromium/Firefox ("at ... (url:line:col)") and Safari ("func@url:line:col").
 *
 * `framesToSkip` is the number of frames above the caller of interest.
 * Frames are counted rather than matched by name, because names are not
 * meaningful in minified code.
 */
function parseCallLocation(
  stack: string | undefined,
  framesToSkip: number
): {
  functionName?: string;
  fileName?: string;
  line?: number;
  column?: number;
} | null {
  if (!stack) return null;

  // Guard against non-standard browser behavior.
  // oxlint-disable-next-line typescript/no-unnecessary-type-conversion
  const frames = String(stack)
    .split("\n")
    .map((l) => l.trim())
    // Chromium prefixes the frames with an "Error" line; other browsers don't.
    .filter((l) => l.startsWith("at ") || l.includes("@"));

  const line = frames.at(framesToSkip);
  if (!line) return null;

  // Chrome/Edge formats:
  //   "at func (https://.../file.js:123:45)"
  //   "at https://.../file.js:123:45"
  let m = line.match(
    /\bat\s+(?:.+?\s+\()?(https?:\/\/[^\s)]+):(\d+):(\d+)\)?$/
  );
  if (m) {
    return {
      fileName: m[1],
      line: Number(m[2]),
      column: Number(m[3]),
    };
  }

  // Firefox/Safari formats:
  //   "func@https://.../file.js:123:45"
  //   "@https://.../file.js:123:45"
  m = line.match(/^(?:([^@]+))?@?(https?:\/\/[^\s)]+):(\d+):(\d+)$/);
  if (m) {
    return {
      functionName: m[1]?.trim(),
      fileName: m[2],
      line: Number(m[3]),
      column: Number(m[4]),
    };
  }

  return null;
}

// CAUTION: If logging from within this function, `originalConsole` must be
// used to avoid recursion.
function emitLog(
  logger: Logger,
  { severityNumber, args, stack, timestamp }: ConsoleCall
) {
  try {
    const log = { body: args.map(String).join(" ") };

    if (!featureFlags.get("otelLogs")) {
      return;
    }

    const severityText = SEVERITY_TEXT[severityNumber];

    // The top frame is the console wrapper, and the next is its caller.
    const loc = parseCallLocation(stack, 1);

    const span = trace.getSpan(context.active());
    const spanContext = span?.spanContext();

    if (severityNumber === SeverityNumber.ERROR && span) {
      span.setStatus({ code: 2, message: log.body });
    }

    const locationAttrs = {
      [SemConv.ATTR_CODE_FILE_PATH]: loc?.fileName,
      [SemConv.ATTR_CODE_FUNCTION_NAME]: loc?.functionName,
      [SemConv.ATTR_CODE_LINE_NUMBER]: loc?.line,
      [SemConv.ATTR_CODE_COLUMN_NUMBER]: loc?.column,
    };

    logger.emit({
      ...log,
      // Pending calls are sent later, so each keeps the time it was made.
      timestamp,
      severityNumber,
      severityText,
      attributes: {
        ...locationAttrs,
        trace_id: spanContext?.traceId,
        span_id: spanContext?.spanId,
        span_sampled: spanContext?.traceFlags === 1,
      },
    });
  } catch (e) {
    // Never allow telemetry to crash app logs
    try {
      originalConsole.warn("otel emitLog failed", e);
    } catch {
      /* no-op */
    }
  }
}

function handleConsoleCall(call: ConsoleCall) {
  if (otelState.kind === "started") {
    emitLog(otelState.logger, call);
  } else if (
    otelState.kind === "pending" &&
    otelState.calls.length < MAX_PENDING_CONSOLE_CALLS
  ) {
    otelState.calls.push(call);
  }
}

function patchConsole() {
  function wrap(
    severityNumber: SeverityNumber,
    original: (...args: unknown[]) => void
  ) {
    return (...args: unknown[]) => {
      // The stack is captured here, rather than in `emitLog`, so that the
      // number of frames above the caller is fixed even if the minifier
      // inlines `emitLog`. This `Error` needs no message because it's only
      // used for its stack trace.
      // oxlint-disable-next-line unicorn/error-message
      const stack = new Error().stack;
      handleConsoleCall({ severityNumber, args, stack, timestamp: Date.now() });
      original(...args);
    };
  }

  console.debug = wrap(SeverityNumber.DEBUG, originalConsole.debug);
  console.log = wrap(SeverityNumber.INFO, originalConsole.log);
  console.info = wrap(SeverityNumber.INFO, originalConsole.info);
  console.warn = wrap(SeverityNumber.WARN, originalConsole.warn);
  console.error = wrap(SeverityNumber.ERROR, originalConsole.error);
}

/**
 * Executes a function within a span context, allowing for tracing of the
 * function's execution and attaching metadata and additional attributes
 * to the span.
 *
 * ### Example
 *
 * ```
 * withSpan("expensive computation", (span) => {
 *   // Perform some expensive computation here
 *   span.setAttribute("computation.type", "fibonacci");
 *
 * ```
 */
export function withSpan<T>(name: string, fn: (span: Span) => T): T;
export function withSpan<T>(
  name: string,
  fn: (span: Span) => Promise<T>
): Promise<T>;
export function withSpan<T>(
  name: string,
  fn: (span: Span) => T | Promise<T>
): T | Promise<T> {
  const tracer = trace.getTracer(TRACER_NAME);
  const span = tracer.startSpan(name, { kind: SpanKind.CLIENT });
  const ctx = trace.setSpan(context.active(), span);

  const recordSuccess = (result: T): T => {
    span.setStatus({ code: SpanStatusCode.OK });
    span.end();
    return result;
  };

  const recordError = (err: unknown): never => {
    const error = err instanceof Error ? err : new Error(String(err));
    span.recordException(error);
    span.setStatus({ code: SpanStatusCode.ERROR, message: error.message });
    span.setAttributes({
      [SemConv.ATTR_EXCEPTION_MESSAGE]: error.message,
      [SemConv.ATTR_EXCEPTION_STACKTRACE]: error.stack,
    });
    span.end();

    throw error;
  };

  return context.with(ctx, () => {
    try {
      const result = fn(span);
      if (result instanceof Promise) {
        return result.then(recordSuccess).catch(recordError);
      } else {
        return recordSuccess(result);
      }
    } catch (err) {
      return recordError(err);
    }
  });
}

type Fn<A extends unknown[], R> = (...args: A) => R;

/**
 * Instruments a function with OpenTelemetry tracing, returning a new wrapped
 * function that will execute within a span context.
 *
 * ### Example
 *
 * ```
 * function foo(a: number, b: number, c: number): number {
 *   return a + b + c;
 * }
 *
 * const bar = instrument("instrumented foo", foo)(1, 2, 3);
 *
 * assert(bar === 6);
 * ```
 *
 * In this example, the `foo` function will be traced and its execution timing
 * recorded. See `withSpan` for usage with access to the span directly.
 */
export function instrument<T extends unknown[], R>(
  name: string,
  fn: Fn<T, R>
): Fn<T, R> {
  return function (...args) {
    return withSpan(name, (_span) => fn(...args));
  };
}
